/**
 * Notice primitive — left-border banner for info/error/success messages.
 * Ported from Play sc-marketing `ui/notice.tsx` (translation log there).
 * Spreads props so consumers can pass `role="alert"` / `role="status"` —
 * REQUIRED where the reskin replaces bare `<p role="alert">` elements.
 *
 * @example
 * <Notice variant="error" role="alert">{error}</Notice>
 */
import type { HTMLAttributes } from 'react';

import { cn } from '../lib/cn';

type NoticeVariant = 'info' | 'error' | 'success' | 'warning';

const VARIANT_CLASSES: Record<NoticeVariant, string> = {
  info: 'border-l-[#00a0d2] bg-[#f0f6fc]',
  error: 'border-l-danger bg-danger-tint text-danger',
  success: 'border-l-success bg-success-tint text-[#007017]',
  // `warning` added L8 round 11: the tokens already existed (--color-warning /
  // -tint, WP's notice-warning amber) but no variant exposed them, so "this is
  // survivable but you should look at it" had to render as `info` or `error`.
  warning: 'border-l-warning bg-warning-tint',
};

interface NoticeProps extends HTMLAttributes<HTMLDivElement> {
  variant?: NoticeVariant;
}

export function Notice({ variant = 'info', className, ...props }: NoticeProps) {
  return <div {...props} className={cn('py-2.5 px-3.5 border-l-4 mb-4', VARIANT_CLASSES[variant], className)} />;
}
